import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CalendarDays, CheckCircle2, TrendingUp, XCircle, Loader2, Crown, Lock, BarChart3 } from "lucide-react";
import { cn } from "@/lib/utils";

const MONTHS_TR = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const fmtMonth = (ym) => {
  const [y, m] = (ym || "").split("-");
  return m ? `${MONTHS_TR[parseInt(m, 10) - 1]} ${y}` : ym;
};

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-xl border border-border bg-card p-5" data-testid={`report-stat-${label}`}>
    <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mb-3", accent)}><Icon className="w-5 h-5" /></div>
    <p className="text-3xl font-bold font-display">{value}</p>
    <p className="text-sm text-muted-foreground mt-0.5">{label}</p>
  </div>
);

export default function Reports() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["reports-summary"],
    queryFn: () => api.get("/reports/summary").then((r) => r.data),
    retry: false,
  });

  const isLocked = error?.response?.status === 402;

  if (isLocked) {
    return (
      <div className="space-y-6" data-testid="reports-page">
        <div>
          <h1 className="text-3xl font-bold">Raporlar</h1>
          <p className="text-muted-foreground mt-1">Randevu ve kazanç özetiniz</p>
        </div>
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-accent/40 p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="reports-pro-lock">
          <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center shrink-0"><Crown className="w-6 h-6 text-primary-foreground" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-lg leading-tight flex items-center gap-2"><Lock className="w-4 h-4" /> Raporlar Pro'ya özel</p>
            <p className="text-sm text-muted-foreground mt-0.5">Randevu, tamamlanan işlem, kazanç ve en çok tercih edilen hizmet raporlarını görün.</p>
          </div>
          <Link to="/panel/abonelik" className="shrink-0">
            <Button className="rounded-full h-11 px-6" data-testid="reports-upgrade-btn"><Crown className="w-4 h-4 mr-1.5" /> Pro'ya Geç</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading || !data)
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const maxRev = Math.max(1, ...data.monthly.map((m) => m.revenue));

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-bold">Raporlar</h1>
        <p className="text-muted-foreground mt-1">Randevu ve kazanç özetiniz</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat icon={CalendarDays} label="Toplam Randevu" value={data.total} accent="bg-indigo-100 text-indigo-600" />
        <Stat icon={CheckCircle2} label="Tamamlanan" value={data.completed} accent="bg-emerald-100 text-emerald-600" />
        <Stat icon={TrendingUp} label="Toplam Kazanç" value={money(data.revenue)} accent="bg-teal-100 text-teal-600" />
        <Stat icon={XCircle} label="İptal Edilen" value={data.cancelled} accent="bg-red-100 text-red-600" />
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-4">Aylık Kazanç</h2>
        {data.monthly.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="no-monthly">Henüz veri yok.</div>
        ) : (
          <div className="rounded-xl border border-border bg-card p-5 space-y-3" data-testid="monthly-chart">
            {data.monthly.map((m) => (
              <div key={m.month} className="flex items-center gap-3">
                <span className="w-20 shrink-0 text-sm text-muted-foreground">{fmtMonth(m.month)}</span>
                <div className="flex-1 bg-secondary rounded-full h-6 overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${Math.max(4, (m.revenue / maxRev) * 100)}%` }} />
                </div>
                <span className="w-24 shrink-0 text-right text-sm font-semibold">{money(m.revenue)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-4">En Çok Tercih Edilen Hizmetler</h2>
        {data.top_services.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="no-top-services">Henüz tamamlanan randevu yok.</div>
        ) : (
          <div className="rounded-xl border border-border bg-card divide-y divide-border overflow-hidden" data-testid="top-services">
            {data.top_services.map((s, i) => (
              <div key={s.name} className="flex items-center gap-3 px-4 py-3" data-testid="top-service-item">
                <span className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                <span className="flex-1 min-w-0 truncate font-medium">{s.name}</span>
                <span className="text-sm text-muted-foreground shrink-0">{s.count} işlem</span>
                <span className="w-24 text-right text-sm font-semibold shrink-0">{money(s.revenue)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
